import { useState, useEffect } from 'react';
import { X, Shield, Users, Crown, UserCheck, UserX, Loader2, Check, AlertCircle, MessageCircle, Ban, Trash2, Megaphone, Flag, Layers, Bug } from 'lucide-react';
import { roleService, UserRole, UserRoleRecord, ModApplication, supabase, bugReportService, BugReport } from '../lib/supabase';

interface AdminPanelProps {
    isOpen: boolean;
    onClose: () => void;
}

// Types for messaging and moderation
interface ModMessage {
    id: string;
    user_id: string;
    subject: string;
    message: string;
    status: 'unread' | 'read' | 'replied' | 'resolved';
    has_new_reply?: boolean;
    last_reply_at?: string;
    created_at: string;
}

interface MessageReply {
    id: string;
    message_id: string;
    sender_id: string;
    sender_role: 'user' | 'mod' | 'admin';
    content: string;
    created_at: string;
}

interface BannedUser {
    id: string;
    user_id: string;
    reason: string;
    banned_by: string;
    banned_at: string;
}

const ROLE_COLORS: Record<UserRole, string> = {
    admin: 'bg-red-500/20 text-red-400 border-red-500/30',
    mod: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    member: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
    user: 'bg-white/10 text-white/60 border-white/20',
};

const ROLE_ICONS: Record<UserRole, React.ReactNode> = {
    admin: <Crown className="size-4" />,
    mod: <Shield className="size-4" />,
    member: <UserCheck className="size-4" />,
    user: <Users className="size-4" />,
};

type TabType = 'roles' | 'applications' | 'assign' | 'messages' | 'moderation' | 'feedback' | 'reports' | 'decks' | 'bugs';

interface UserFeedback {
    id: string;
    user_id: string | null;
    category: string;
    message: string;
    created_at: string;
}

interface UserReport {
    id: string;
    reporter_id: string;
    reported_user_id: string;
    reason: string;
    details: string | null;
    status: string;
    created_at: string;
    resolved_at: string | null;
    resolved_by: string | null;
    resolution_notes: string | null;
}

export function AdminPanel({ isOpen, onClose }: AdminPanelProps) {
    const [activeTab, setActiveTab] = useState<TabType>('roles');
    const [loading, setLoading] = useState(true);
    const [roles, setRoles] = useState<UserRoleRecord[]>([]);
    const [applications, setApplications] = useState<ModApplication[]>([]);
    const [messages, setMessages] = useState<ModMessage[]>([]);
    const [bannedUsers, setBannedUsers] = useState<BannedUser[]>([]);
    const [feedback, setFeedback] = useState<UserFeedback[]>([]);
    const [reports, setReports] = useState<UserReport[]>([]);
    const [decks, setDecks] = useState<any[]>([]);
    const [bugs, setBugs] = useState<BugReport[]>([]);
    const [modCount, setModCount] = useState(0);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Permission state
    const [isAdmin, setIsAdmin] = useState(false);

    // For assigning roles
    const [assignUserId, setAssignUserId] = useState('');
    const [assignRole, setAssignRole] = useState<UserRole>('member');
    const [assignNotes, setAssignNotes] = useState('');
    const [assignLoading, setAssignLoading] = useState(false);

    // For moderation
    const [banUserId, setBanUserId] = useState('');
    const [banReason, setBanReason] = useState('');
    const [banLoading, setBanLoading] = useState(false);

    // For message thread view
    const [selectedMessage, setSelectedMessage] = useState<ModMessage | null>(null);
    const [messageReplies, setMessageReplies] = useState<MessageReply[]>([]);
    const [replyText, setReplyText] = useState('');
    const [replyLoading, setReplyLoading] = useState(false);

    useEffect(() => {
        if (isOpen) {
            loadData();
            roleService.hasRole('admin').then(setIsAdmin);
        }
    }, [isOpen]);

    const loadData = async () => {
        setLoading(true);
        setError('');
        try {
            const [rolesData, appsData, count] = await Promise.all([
                roleService.getAllRoles(),
                roleService.getPendingApplications(),
                roleService.getModCount(),
            ]);
            setRoles(rolesData);
            setApplications(appsData);
            setModCount(count);

            // Load messages
            const { data: msgData } = await supabase
                .from('mod_messages')
                .select('*')
                .order('created_at', { ascending: false });
            if (msgData) setMessages(msgData as ModMessage[]);

            // Load banned users
            const { data: banData } = await supabase
                .from('banned_users')
                .select('*')
                .order('banned_at', { ascending: false });
            if (banData) setBannedUsers(banData as BannedUser[]);

            // Load feedback
            const { data: feedbackData } = await supabase
                .from('user_feedback')
                .select('*')
                .order('created_at', { ascending: false });
            if (feedbackData) setFeedback(feedbackData as UserFeedback[]);

            // Load user reports
            const { data: reportsData } = await supabase
                .from('user_reports')
                .select('*')
                .order('created_at', { ascending: false });
            if (reportsData) setReports(reportsData as UserReport[]);

            // Load all shared decks for management
            const { data: decksData } = await supabase
                .from('decks')
                .select('*')
                .eq('is_public', true)
                .order('created_at', { ascending: false })
                .limit(100);
            if (decksData) setDecks(decksData);

            // Load bug reports
            const bugsData = await bugReportService.getAllBugReports();
            setBugs(bugsData);
        } catch (err: any) {
            setError(err.message || 'Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    const handleApprove = async (applicationId: string) => {
        try {
            await roleService.approveApplication(applicationId);
            setSuccess('Application approved!');
            loadData();
        } catch (err: any) {
            setError(err.message || 'Failed to approve');
        }
    };

    const handleReject = async (applicationId: string) => {
        try {
            await roleService.rejectApplication(applicationId);
            setSuccess('Application rejected');
            loadData();
        } catch (err: any) {
            setError(err.message || 'Failed to reject');
        }
    };

    const handleAssignRole = async () => {
        if (!assignUserId.trim()) {
            setError('Please enter a user ID');
            return;
        }

        setAssignLoading(true);
        setError('');
        try {
            await roleService.assignRole(assignUserId.trim(), assignRole, assignNotes);
            setSuccess(`Role ${assignRole} assigned successfully!`);
            setAssignUserId('');
            setAssignNotes('');
            loadData();
        } catch (err: any) {
            setError(err.message || 'Failed to assign role');
        } finally {
            setAssignLoading(false);
        }
    };

    const handleRemoveRole = async (userId: string) => {
        if (!confirm('Remove this role? User will become a regular user.')) return;

        try {
            await roleService.removeRole(userId);
            setSuccess('Role removed');
            loadData();
        } catch (err: any) {
            setError(err.message || 'Failed to remove role');
        }
    };

    const handleBanUser = async () => {
        if (!banUserId.trim()) {
            setError('Please enter a user ID');
            return;
        }

        setBanLoading(true);
        setError('');
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) throw new Error('Not authenticated');

            // Insert ban record
            const { error: banError } = await supabase
                .from('banned_users')
                .insert({
                    user_id: banUserId.trim(),
                    reason: banReason || 'No reason provided',
                    banned_by: user.id,
                });

            if (banError) throw banError;

            // Delete all user's decks
            await supabase
                .from('decks')
                .delete()
                .eq('user_id', banUserId.trim());

            // Remove any roles
            await roleService.removeRole(banUserId.trim());

            setSuccess('User banned and all their decks removed');
            setBanUserId('');
            setBanReason('');
            loadData();
        } catch (err: any) {
            setError(err.message || 'Failed to ban user');
        } finally {
            setBanLoading(false);
        }
    };

    const handleUnbanUser = async (banId: string, userId: string) => {
        if (!confirm('Unban this user?')) return;

        try {
            await supabase.from('banned_users').delete().eq('id', banId);
            setSuccess('User unbanned');
            loadData();
        } catch (err: any) {
            setError(err.message || 'Failed to unban');
        }
    };

    const handleDeleteDeck = async (deckId: string, deckName: string) => {
        if (!confirm(`Delete deck "${deckName}"? This cannot be undone.`)) return;

        try {
            const { error: deleteError } = await supabase
                .from('decks')
                .delete()
                .eq('id', deckId);

            if (deleteError) throw deleteError;

            setSuccess(`Deck "${deckName}" deleted`);
            setDecks(decks.filter(d => d.id !== deckId));
        } catch (err: any) {
            setError(err.message || 'Failed to delete deck');
        }
    };

    const handleMarkMessageRead = async (msgId: string) => {
        try {
            await supabase
                .from('mod_messages')
                .update({ status: 'read' })
                .eq('id', msgId);
            loadData();
        } catch (err: any) {
            setError(err.message || 'Failed to update message');
        }
    };

    // Resolve = Delete the entire conversation
    const handleResolveMessage = async (msgId: string) => {
        if (!confirm('Resolve and delete this conversation? This cannot be undone.')) return;

        try {
            // Replies cascade delete via FK, so just delete the message
            const { error: deleteError } = await supabase
                .from('mod_messages')
                .delete()
                .eq('id', msgId);

            if (deleteError) throw deleteError;

            setSuccess('Conversation resolved and deleted');
            setSelectedMessage(null);

            // Force refresh the messages list
            const { data: msgData } = await supabase
                .from('mod_messages')
                .select('*')
                .order('created_at', { ascending: false });
            if (msgData) setMessages(msgData as ModMessage[]);
        } catch (err: any) {
            setError(err.message || 'Failed to resolve message');
        }
    };

    // Open a message thread
    const handleOpenThread = async (msg: ModMessage) => {
        setSelectedMessage(msg);
        setReplyText('');

        // Mark as read if unread
        if (msg.status === 'unread') {
            await supabase
                .from('mod_messages')
                .update({ status: 'read' })
                .eq('id', msg.id);
        }

        // Load replies
        const { data } = await supabase
            .from('mod_message_replies')
            .select('*')
            .eq('message_id', msg.id)
            .order('created_at', { ascending: true });

        if (data) setMessageReplies(data as MessageReply[]);
    };

    // Send a reply
    const handleSendReply = async () => {
        if (!selectedMessage || !replyText.trim()) return;

        setReplyLoading(true);
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) throw new Error('Not authenticated');

            // Get current user's role
            const role = await roleService.getCurrentUserRole();

            const { error: insertError } = await supabase
                .from('mod_message_replies')
                .insert({
                    message_id: selectedMessage.id,
                    sender_id: user.id,
                    sender_role: role,
                    content: replyText.trim(),
                });

            if (insertError) throw insertError;

            // Update message status to 'replied' and mark has_new_reply for user
            await supabase
                .from('mod_messages')
                .update({
                    status: 'replied',
                    has_new_reply: true,
                    last_reply_at: new Date().toISOString(),
                })
                .eq('id', selectedMessage.id);

            setReplyText('');
            await handleOpenThread(selectedMessage); // Refresh replies
            loadData();
        } catch (err: any) {
            setError(err.message || 'Failed to send reply');
        } finally {
            setReplyLoading(false);
        }
    };

    if (!isOpen) return null;

    const unreadCount = messages.filter(m => m.status === 'unread').length;

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-slate-900 border border-white/10 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-white/10">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-red-500/20 rounded-lg">
                            <Crown className="size-5 text-red-400" />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-white">Admin Panel</h2>
                            <p className="text-sm text-white/50">Manage roles, messages, and moderation</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
                        <X className="size-5 text-white/60" />
                    </button>
                </div>

                {/* Stats bar */}
                <div className="flex items-center gap-6 px-4 py-3 bg-white/5 border-b border-white/10 flex-wrap">
                    <div className="text-sm">
                        <span className="text-white/50">Mods:</span>
                        <span className={`ml-2 font-bold ${modCount >= 30 ? 'text-red-400' : 'text-purple-400'}`}>
                            {modCount}/30
                        </span>
                    </div>
                    <div className="text-sm">
                        <span className="text-white/50">Applications:</span>
                        <span className="ml-2 font-bold text-cyan-400">
                            {applications.length}
                        </span>
                    </div>
                    <div className="text-sm">
                        <span className="text-white/50">Messages:</span>
                        <span className={`ml-2 font-bold ${unreadCount > 0 ? 'text-amber-400' : 'text-white/40'}`}>
                            {unreadCount} unread
                        </span>
                    </div>
                    <div className="text-sm">
                        <span className="text-white/50">Banned:</span>
                        <span className="ml-2 font-bold text-red-400">
                            {bannedUsers.length}
                        </span>
                    </div>
                </div>

                {/* Tabs - compact layout to fit all without scrolling */}
                <div className="flex flex-wrap border-b border-white/10">
                    {/* Standard tabs for all staff */}
                    {[
                        { id: 'roles' as TabType, label: 'Roles', icon: <Users className="size-3.5" /> },
                        { id: 'messages' as TabType, label: unreadCount > 0 ? `Msgs (${unreadCount})` : 'Msgs', icon: <MessageCircle className="size-3.5" /> },
                        { id: 'feedback' as TabType, label: feedback.length > 0 ? `FB (${feedback.length})` : 'FB', icon: <Megaphone className="size-3.5" /> },
                        { id: 'reports' as TabType, label: reports.filter(r => r.status === 'pending').length > 0 ? `Rpts (${reports.filter(r => r.status === 'pending').length})` : 'Rpts', icon: <Flag className="size-3.5" /> },
                        { id: 'bugs' as TabType, label: bugs.filter(b => b.status === 'new').length > 0 ? `Bugs (${bugs.filter(b => b.status === 'new').length})` : 'Bugs', icon: <Bug className="size-3.5" /> },
                        { id: 'decks' as TabType, label: decks.length > 0 ? `Decks (${decks.length})` : 'Decks', icon: <Layers className="size-3.5" /> },
                        { id: 'moderation' as TabType, label: 'Mod', icon: <Ban className="size-3.5" /> },
                    ].map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium transition-colors whitespace-nowrap ${activeTab === tab.id
                                ? 'text-cyan-400 border-b-2 border-cyan-400 bg-cyan-500/10'
                                : 'text-white/60 hover:text-white/80'
                                }`}
                        >
                            {tab.icon}
                            {tab.label}
                        </button>
                    ))}

                    {/* Admin-only tabs */}
                    {isAdmin && (
                        <>
                            <button
                                onClick={() => setActiveTab('assign')}
                                className={`flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium transition-colors whitespace-nowrap ${activeTab === 'assign'
                                    ? 'text-cyan-400 border-b-2 border-cyan-400 bg-cyan-500/10'
                                    : 'text-white/60 hover:text-white/80'
                                    }`}
                            >
                                <UserCheck className="size-3.5" />
                                Assign
                            </button>
                            <button
                                onClick={() => setActiveTab('applications')}
                                className={`flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium transition-colors whitespace-nowrap ${activeTab === 'applications'
                                    ? 'text-red-400 border-b-2 border-red-400 bg-red-500/10'
                                    : 'text-red-400/60 hover:text-red-400/80'
                                    }`}
                            >
                                <Shield className="size-3.5" />
                                Apps ({applications.length})
                            </button>
                        </>
                    )}
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-4">
                    {/* Alerts */}
                    {error && (
                        <div className="mb-4 p-3 bg-red-500/20 border border-red-500/30 rounded-lg flex items-center gap-2 text-red-400">
                            <AlertCircle className="size-4" />
                            {error}
                            <button onClick={() => setError('')} className="ml-auto">
                                <X className="size-4" />
                            </button>
                        </div>
                    )}
                    {success && (
                        <div className="mb-4 p-3 bg-green-500/20 border border-green-500/30 rounded-lg flex items-center gap-2 text-green-400">
                            <Check className="size-4" />
                            {success}
                            <button onClick={() => setSuccess('')} className="ml-auto">
                                <X className="size-4" />
                            </button>
                        </div>
                    )}

                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="size-8 animate-spin text-cyan-400" />
                        </div>
                    ) : (
                        <>
                            {/* Roles Tab */}
                            {activeTab === 'roles' && (
                                <div className="space-y-3">
                                    {roles.length === 0 ? (
                                        <p className="text-center text-white/40 py-8">No roles assigned yet</p>
                                    ) : (
                                        roles.map(role => (
                                            <div
                                                key={role.id}
                                                className="flex items-center justify-between p-4 bg-white/5 rounded-lg border border-white/10"
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div className={`p-2 rounded-lg border ${ROLE_COLORS[role.role]}`}>
                                                        {ROLE_ICONS[role.role]}
                                                    </div>
                                                    <div>
                                                        <div className="flex items-center gap-2">
                                                            <span className={`px-2 py-0.5 rounded text-xs font-medium uppercase ${ROLE_COLORS[role.role]}`}>
                                                                {role.role}
                                                            </span>
                                                        </div>
                                                        {/* Identity visibility: admins see all, mods see only members */}
                                                        {(isAdmin || role.role === 'member') ? (
                                                            <p className="text-xs text-white/40 mt-1 font-mono">
                                                                {role.user_id}
                                                            </p>
                                                        ) : (
                                                            <p className="text-xs text-white/30 mt-1 italic">
                                                                Staff · Identity Protected
                                                            </p>
                                                        )}
                                                        {role.notes && (
                                                            <p className="text-xs text-white/50 mt-0.5">
                                                                <span className="text-white/30">Notes:</span> {role.notes}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                                {role.role !== 'admin' && (
                                                    <button
                                                        onClick={() => handleRemoveRole(role.user_id)}
                                                        className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors"
                                                    >
                                                        <UserX className="size-4" />
                                                    </button>
                                                )}
                                            </div>
                                        ))
                                    )}
                                </div>
                            )}

                            {/* Applications Tab */}
                            {activeTab === 'applications' && (
                                <div className="space-y-3">
                                    {applications.length === 0 ? (
                                        <p className="text-center text-white/40 py-8">No pending applications</p>
                                    ) : (
                                        applications.map(app => (
                                            <div
                                                key={app.id}
                                                className="p-4 bg-white/5 rounded-lg border border-white/10"
                                            >
                                                <div className="flex items-start justify-between">
                                                    <div>
                                                        <p className="text-xs text-white/40">
                                                            ID: {app.user_id.slice(0, 8)}...
                                                        </p>
                                                        <p className="text-xs text-white/30 mt-1">
                                                            Applied: {new Date(app.created_at).toLocaleDateString()}
                                                        </p>
                                                    </div>
                                                    <div className="flex gap-2">
                                                        <button
                                                            onClick={() => handleApprove(app.id)}
                                                            disabled={modCount >= 30}
                                                            className="flex items-center gap-1 px-3 py-1.5 bg-green-600 hover:bg-green-500 disabled:opacity-50 text-white text-sm rounded-lg transition-colors"
                                                        >
                                                            <Check className="size-3" />
                                                            Approve
                                                        </button>
                                                        <button
                                                            onClick={() => handleReject(app.id)}
                                                            className="flex items-center gap-1 px-3 py-1.5 bg-red-600/50 hover:bg-red-500 text-white text-sm rounded-lg transition-colors"
                                                        >
                                                            <X className="size-3" />
                                                            Reject
                                                        </button>
                                                    </div>
                                                </div>
                                                <div className="mt-3 p-3 bg-black/20 rounded-lg">
                                                    <p className="text-sm text-white/70">{app.reason}</p>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            )}

                            {/* Messages Tab */}
                            {activeTab === 'messages' && (
                                <div className="flex gap-4 min-h-[400px]">
                                    {/* Message list */}
                                    <div className={`${selectedMessage ? 'w-1/3' : 'w-full'} space-y-2 overflow-y-auto max-h-[500px] pr-2`}>
                                        {messages.length === 0 ? (
                                            <p className="text-center text-white/40 py-8">No messages from users</p>
                                        ) : (
                                            messages.map(msg => (
                                                <button
                                                    key={msg.id}
                                                    onClick={() => handleOpenThread(msg)}
                                                    className={`w-full text-left p-3 rounded-lg border transition-colors ${selectedMessage?.id === msg.id
                                                        ? 'bg-cyan-500/20 border-cyan-500/50'
                                                        : msg.status === 'unread'
                                                            ? 'bg-amber-500/10 border-amber-500/30 hover:bg-amber-500/20'
                                                            : msg.status === 'replied'
                                                                ? 'bg-purple-500/10 border-purple-500/30 hover:bg-purple-500/20'
                                                                : 'bg-white/5 border-white/10 hover:bg-white/10'
                                                        }`}
                                                >
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <span className={`w-2 h-2 rounded-full ${msg.status === 'unread' ? 'bg-amber-400' :
                                                            msg.status === 'replied' ? 'bg-purple-400' : 'bg-white/30'
                                                            }`} />
                                                        <span className="text-sm text-white font-medium truncate">{msg.subject}</span>
                                                    </div>
                                                    <p className="text-xs text-white/40 truncate">{msg.message}</p>
                                                    <p className="text-xs text-white/30 mt-1">
                                                        {new Date(msg.created_at).toLocaleDateString()}
                                                    </p>
                                                </button>
                                            ))
                                        )}
                                    </div>

                                    {/* Thread view */}
                                    {selectedMessage && (
                                        <div className="flex-1 flex flex-col bg-black/20 rounded-lg border border-white/10">
                                            {/* Thread header */}
                                            <div className="p-4 border-b border-white/10 flex items-start justify-between">
                                                <div>
                                                    <h4 className="text-white font-medium">{selectedMessage.subject}</h4>
                                                    <p className="text-xs text-white/40">
                                                        User: {selectedMessage.user_id.slice(0, 8)}... • {new Date(selectedMessage.created_at).toLocaleString()}
                                                    </p>
                                                </div>
                                                <div className="flex gap-2">
                                                    <button
                                                        onClick={() => handleResolveMessage(selectedMessage.id)}
                                                        className="px-3 py-1.5 text-xs bg-green-600/50 hover:bg-green-500 text-white rounded transition-colors flex items-center gap-1"
                                                    >
                                                        <Trash2 className="size-3" />
                                                        Resolve & Delete
                                                    </button>
                                                    <button
                                                        onClick={() => setSelectedMessage(null)}
                                                        className="p-1.5 text-white/40 hover:text-white/60"
                                                    >
                                                        <X className="size-4" />
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Messages */}
                                            <div className="flex-1 p-4 space-y-3 overflow-y-auto max-h-[300px]">
                                                {/* Original message */}
                                                <div className="flex gap-3">
                                                    <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-xs text-white/60">
                                                        U
                                                    </div>
                                                    <div className="flex-1 bg-white/5 rounded-lg p-3">
                                                        <p className="text-sm text-white/70">{selectedMessage.message}</p>
                                                        <p className="text-xs text-white/30 mt-1">{new Date(selectedMessage.created_at).toLocaleTimeString()}</p>
                                                    </div>
                                                </div>

                                                {/* Replies */}
                                                {messageReplies.map(reply => (
                                                    <div key={reply.id} className={`flex gap-3 ${reply.sender_role !== 'user' ? 'flex-row-reverse' : ''}`}>
                                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs ${reply.sender_role === 'admin' ? 'bg-red-500/20 text-red-400' :
                                                            reply.sender_role === 'mod' ? 'bg-purple-500/20 text-purple-400' :
                                                                'bg-white/10 text-white/60'
                                                            }`}>
                                                            {reply.sender_role === 'admin' ? 'A' : reply.sender_role === 'mod' ? 'M' : 'U'}
                                                        </div>
                                                        <div className={`flex-1 max-w-[80%] rounded-lg p-3 ${reply.sender_role !== 'user'
                                                            ? 'bg-purple-500/10 border border-purple-500/20'
                                                            : 'bg-white/5'
                                                            }`}>
                                                            <p className="text-sm text-white/70">{reply.content}</p>
                                                            <p className="text-xs text-white/30 mt-1">
                                                                {reply.sender_role !== 'user' && <span className="text-purple-400">{reply.sender_role} • </span>}
                                                                {new Date(reply.created_at).toLocaleTimeString()}
                                                            </p>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>

                                            {/* Reply input */}
                                            <div className="p-4 border-t border-white/10">
                                                <div className="flex gap-2">
                                                    <input
                                                        type="text"
                                                        value={replyText}
                                                        onChange={(e) => setReplyText(e.target.value)}
                                                        onKeyDown={(e) => e.key === 'Enter' && handleSendReply()}
                                                        placeholder="Type your reply..."
                                                        className="flex-1 px-4 py-2 bg-black/30 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-purple-500/50"
                                                    />
                                                    <button
                                                        onClick={handleSendReply}
                                                        disabled={replyLoading || !replyText.trim()}
                                                        className="px-4 py-2 bg-purple-600 hover:bg-purple-500 disabled:opacity-50 text-white text-sm rounded-lg transition-colors"
                                                    >
                                                        {replyLoading ? <Loader2 className="size-4 animate-spin" /> : 'Send'}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Feedback Tab */}
                            {activeTab === 'feedback' && (
                                <div className="space-y-3">
                                    {feedback.length === 0 ? (
                                        <p className="text-center text-white/40 py-8">No feedback received yet</p>
                                    ) : (
                                        feedback.map(fb => (
                                            <div
                                                key={fb.id}
                                                className="p-4 rounded-lg border bg-white/5 border-white/10"
                                            >
                                                <div className="flex items-start justify-between">
                                                    <div>
                                                        <div className="flex items-center gap-2">
                                                            <span className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${fb.category === 'bug' ? 'bg-red-500/20 text-red-400' :
                                                                fb.category === 'feature' ? 'bg-green-500/20 text-green-400' :
                                                                    'bg-white/10 text-white/50'
                                                                }`}>
                                                                {fb.category}
                                                            </span>
                                                            <span className="text-xs text-white/30">
                                                                {new Date(fb.created_at).toLocaleString()}
                                                            </span>
                                                        </div>
                                                        <p className="text-xs text-white/40 mt-1">
                                                            {fb.user_id ? `User: ${fb.user_id.slice(0, 8)}...` : 'Anonymous'}
                                                        </p>
                                                    </div>
                                                    {isAdmin && (
                                                        <button
                                                            onClick={async () => {
                                                                await supabase.from('user_feedback').delete().eq('id', fb.id);
                                                                loadData();
                                                            }}
                                                            className="p-1.5 text-white/30 hover:text-red-400 transition-colors"
                                                            title="Delete feedback"
                                                        >
                                                            <Trash2 className="size-4" />
                                                        </button>
                                                    )}
                                                </div>
                                                <div className="mt-3 p-3 bg-black/20 rounded-lg">
                                                    <p className="text-sm text-white/70">{fb.message}</p>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            )}

                            {/* Reports Tab */}
                            {activeTab === 'reports' && (
                                <div className="space-y-3">
                                    {reports.length === 0 ? (
                                        <p className="text-center text-white/40 py-8">No reports received yet</p>
                                    ) : (
                                        reports.map(report => (
                                            <div
                                                key={report.id}
                                                className={`p-4 rounded-lg border ${report.status === 'pending'
                                                    ? 'bg-orange-500/10 border-orange-500/20'
                                                    : 'bg-white/5 border-white/10'
                                                    }`}
                                            >
                                                <div className="flex items-start justify-between">
                                                    <div>
                                                        <div className="flex items-center gap-2">
                                                            <span className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${report.reason === 'harassment' ? 'bg-red-500/20 text-red-400' :
                                                                report.reason === 'spam' ? 'bg-yellow-500/20 text-yellow-400' :
                                                                    report.reason === 'cheating' ? 'bg-purple-500/20 text-purple-400' :
                                                                        'bg-white/10 text-white/50'
                                                                }`}>
                                                                {report.reason.replace('_', ' ')}
                                                            </span>
                                                            <span className={`px-2 py-0.5 rounded text-xs ${report.status === 'pending' ? 'bg-orange-500/20 text-orange-400' :
                                                                report.status === 'reviewed' ? 'bg-blue-500/20 text-blue-400' :
                                                                    'bg-green-500/20 text-green-400'
                                                                }`}>
                                                                {report.status}
                                                            </span>
                                                            <span className="text-xs text-white/30">
                                                                {new Date(report.created_at).toLocaleString()}
                                                            </span>
                                                        </div>
                                                        <p className="text-xs text-white/40 mt-1">
                                                            Reporter: {report.reporter_id.slice(0, 8)}... → Reported: {report.reported_user_id.slice(0, 8)}...
                                                        </p>
                                                    </div>
                                                    <button
                                                        onClick={async () => {
                                                            await supabase.from('user_reports').delete().eq('id', report.id);
                                                            setSuccess('Report resolved and removed');
                                                            loadData();
                                                        }}
                                                        className="px-3 py-1.5 text-xs bg-green-600/50 hover:bg-green-500 text-white rounded-lg transition-colors"
                                                    >
                                                        Resolve
                                                    </button>
                                                </div>
                                                {report.details && (
                                                    <div className="mt-3 p-3 bg-black/20 rounded-lg">
                                                        <p className="text-sm text-white/70">{report.details}</p>
                                                    </div>
                                                )}
                                            </div>
                                        ))
                                    )}
                                </div>
                            )}

                            {/* Bugs Tab */}
                            {activeTab === 'bugs' && (
                                <div className="space-y-3">
                                    <p className="text-sm text-white/40 mb-4">
                                        Bug reports from users. Review and update status.
                                    </p>
                                    {bugs.length === 0 ? (
                                        <p className="text-center text-white/40 py-8">No bug reports yet 🎉</p>
                                    ) : (
                                        bugs.map(bug => (
                                            <div
                                                key={bug.id}
                                                className={`p-4 rounded-lg border ${bug.status === 'new'
                                                        ? 'bg-amber-500/10 border-amber-500/20'
                                                        : bug.status === 'in_progress'
                                                            ? 'bg-blue-500/10 border-blue-500/20'
                                                            : 'bg-white/5 border-white/10'
                                                    }`}
                                            >
                                                <div className="flex items-start justify-between">
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${bug.status === 'new' ? 'bg-amber-500/20 text-amber-400' :
                                                                    bug.status === 'in_progress' ? 'bg-blue-500/20 text-blue-400' :
                                                                        bug.status === 'resolved' ? 'bg-green-500/20 text-green-400' :
                                                                            'bg-white/10 text-white/50'
                                                                }`}>
                                                                {bug.status.replace('_', ' ')}
                                                            </span>
                                                            <span className="text-xs text-white/30">
                                                                {new Date(bug.created_at).toLocaleString()}
                                                            </span>
                                                        </div>
                                                        <p className="text-xs text-white/40 mt-1">
                                                            From: {bug.display_name} {bug.user_id && `(${bug.user_id.slice(0, 8)}...)`}
                                                        </p>
                                                        <p className="text-sm font-medium text-cyan-400 mt-2">
                                                            📍 {bug.location}
                                                        </p>
                                                    </div>
                                                    <div className="flex gap-1 ml-2">
                                                        {bug.status === 'new' && (
                                                            <button
                                                                onClick={async () => {
                                                                    await bugReportService.updateBugStatus(bug.id, 'in_progress');
                                                                    loadData();
                                                                }}
                                                                className="px-2 py-1 text-xs bg-blue-600/50 hover:bg-blue-500 text-white rounded transition-colors"
                                                                title="Mark as in progress"
                                                            >
                                                                Start
                                                            </button>
                                                        )}
                                                        {(bug.status === 'new' || bug.status === 'in_progress') && (
                                                            <>
                                                                <button
                                                                    onClick={async () => {
                                                                        await bugReportService.updateBugStatus(bug.id, 'resolved', 'Fixed');
                                                                        loadData();
                                                                    }}
                                                                    className="px-2 py-1 text-xs bg-green-600/50 hover:bg-green-500 text-white rounded transition-colors"
                                                                    title="Mark as resolved"
                                                                >
                                                                    ✓ Fixed
                                                                </button>
                                                                <button
                                                                    onClick={async () => {
                                                                        await bugReportService.updateBugStatus(bug.id, 'wont_fix', 'Not a bug or not actionable');
                                                                        loadData();
                                                                    }}
                                                                    className="px-2 py-1 text-xs bg-white/10 hover:bg-white/20 text-white/60 rounded transition-colors"
                                                                    title="Won't fix"
                                                                >
                                                                    Skip
                                                                </button>
                                                            </>
                                                        )}
                                                        {isAdmin && (
                                                            <button
                                                                onClick={async () => {
                                                                    if (confirm('Delete this bug report?')) {
                                                                        await bugReportService.deleteBugReport(bug.id);
                                                                        loadData();
                                                                    }
                                                                }}
                                                                className="p-1 text-red-400/60 hover:text-red-400 transition-colors"
                                                                title="Delete"
                                                            >
                                                                <Trash2 className="size-3.5" />
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="mt-3 p-3 bg-black/20 rounded-lg">
                                                    <p className="text-sm text-white/70">{bug.description}</p>
                                                    {bug.expected && (
                                                        <p className="text-xs text-white/40 mt-2">
                                                            <span className="text-white/30">Expected:</span> {bug.expected}
                                                        </p>
                                                    )}
                                                </div>
                                                {bug.resolution_notes && (
                                                    <p className="text-xs text-green-400/70 mt-2">
                                                        ✓ {bug.resolution_notes}
                                                    </p>
                                                )}
                                            </div>
                                        ))
                                    )}
                                </div>
                            )}

                            {/* Decks Tab */}
                            {activeTab === 'decks' && (
                                <div className="space-y-3">
                                    <p className="text-sm text-white/40 mb-4">
                                        Manage shared decks. Delete inappropriate or spam content.
                                    </p>
                                    {decks.length === 0 ? (
                                        <p className="text-center text-white/40 py-8">No shared decks yet</p>
                                    ) : (
                                        decks.map(deck => (
                                            <div
                                                key={deck.id}
                                                className="flex items-center justify-between p-4 bg-white/5 rounded-lg border border-white/10"
                                            >
                                                <div className="flex-1 min-w-0">
                                                    <p className="font-medium text-white truncate">{deck.name}</p>
                                                    <div className="flex items-center gap-2 text-xs text-white/40 mt-1">
                                                        <span>by {deck.author_name || 'Anonymous'}</span>
                                                        <span>•</span>
                                                        <span>{deck.format}</span>
                                                        <span>•</span>
                                                        <span>{deck.deck_size || 0} cards</span>
                                                        <span>•</span>
                                                        <span>{new Date(deck.created_at).toLocaleDateString()}</span>
                                                    </div>
                                                    {deck.user_id && (
                                                        <p className="text-[10px] text-white/20 mt-0.5 font-mono">
                                                            User: {deck.user_id.slice(0, 8)}...
                                                        </p>
                                                    )}
                                                </div>
                                                <button
                                                    onClick={() => handleDeleteDeck(deck.id, deck.name)}
                                                    className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors ml-3 flex-shrink-0"
                                                    title="Delete deck"
                                                >
                                                    <Trash2 className="size-4" />
                                                </button>
                                            </div>
                                        ))
                                    )}
                                </div>
                            )}

                            {/* Moderation Tab */}
                            {activeTab === 'moderation' && (
                                <div className="space-y-6">
                                    {/* Ban User Section */}
                                    <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                                        <h3 className="font-medium text-red-400 mb-3 flex items-center gap-2">
                                            <Ban className="size-4" />
                                            Ban User
                                        </h3>
                                        <div className="space-y-3">
                                            <input
                                                type="text"
                                                value={banUserId}
                                                onChange={(e) => setBanUserId(e.target.value)}
                                                placeholder="Enter user UUID..."
                                                className="w-full px-4 py-2 bg-black/20 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-red-500/50"
                                            />
                                            <input
                                                type="text"
                                                value={banReason}
                                                onChange={(e) => setBanReason(e.target.value)}
                                                placeholder="Reason for ban..."
                                                className="w-full px-4 py-2 bg-black/20 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-red-500/50"
                                            />
                                            <button
                                                onClick={handleBanUser}
                                                disabled={banLoading || !banUserId.trim()}
                                                className="w-full py-2 bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                                            >
                                                {banLoading ? <Loader2 className="size-4 animate-spin" /> : <Ban className="size-4" />}
                                                Ban User & Delete All Decks
                                            </button>
                                            <p className="text-xs text-white/30">
                                                This will ban the user and permanently delete all their decks.
                                            </p>
                                        </div>
                                    </div>

                                    {/* Banned Users List */}
                                    <div>
                                        <h3 className="font-medium text-white/80 mb-3">Banned Users ({bannedUsers.length})</h3>
                                        {bannedUsers.length === 0 ? (
                                            <p className="text-center text-white/40 py-4">No banned users</p>
                                        ) : (
                                            <div className="space-y-2">
                                                {bannedUsers.map(ban => (
                                                    <div key={ban.id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                                                        <div>
                                                            <p className="text-sm text-white/60">ID: {ban.user_id.slice(0, 8)}...</p>
                                                            <p className="text-xs text-white/40">
                                                                <span className="text-white/30">Reason:</span> {ban.reason}
                                                            </p>
                                                            <p className="text-xs text-white/30">
                                                                {new Date(ban.banned_at).toLocaleDateString()}
                                                            </p>
                                                        </div>
                                                        <button
                                                            onClick={() => handleUnbanUser(ban.id, ban.user_id)}
                                                            className="px-3 py-1.5 text-sm bg-green-600/50 hover:bg-green-500 text-white rounded-lg transition-colors"
                                                        >
                                                            Unban
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Assign Tab */}
                            {activeTab === 'assign' && (
                                <div className="max-w-md mx-auto space-y-4">
                                    <div>
                                        <label className="block text-sm text-white/60 mb-2">User ID</label>
                                        <input
                                            type="text"
                                            value={assignUserId}
                                            onChange={(e) => setAssignUserId(e.target.value)}
                                            placeholder="Enter user UUID..."
                                            className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-cyan-500/50"
                                        />
                                        <p className="text-xs text-white/30 mt-1">
                                            Find user IDs in Supabase Auth dashboard
                                        </p>
                                    </div>

                                    <div>
                                        <label className="block text-sm text-white/60 mb-2">Role</label>
                                        <div className="flex gap-2">
                                            {(['member', 'mod'] as UserRole[]).map(role => (
                                                <button
                                                    key={role}
                                                    onClick={() => setAssignRole(role)}
                                                    className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${assignRole === role
                                                        ? ROLE_COLORS[role]
                                                        : 'border-white/10 text-white/40 hover:text-white/60'
                                                        }`}
                                                >
                                                    {ROLE_ICONS[role]}
                                                    <span className="capitalize">{role}</span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm text-white/60 mb-2">Notes (optional)</label>
                                        <input
                                            type="text"
                                            value={assignNotes}
                                            onChange={(e) => setAssignNotes(e.target.value)}
                                            placeholder="Reason for assignment..."
                                            className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-cyan-500/50"
                                        />
                                    </div>

                                    <button
                                        onClick={handleAssignRole}
                                        disabled={assignLoading || !assignUserId.trim()}
                                        className="w-full py-3 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 disabled:opacity-50 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                                    >
                                        {assignLoading ? (
                                            <Loader2 className="size-4 animate-spin" />
                                        ) : (
                                            <UserCheck className="size-4" />
                                        )}
                                        Assign Role
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
